import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  WorkOutline,
  ApartmentOutlined,
  PersonOutline,
  EventOutlined,
  HistoryToggleOffOutlined,
  PlaceOutlined,
  PublicOutlined,
  MailOutline,
} from "@mui/icons-material";
import Navbar from "../../components/navbar/Navbar";
import Sidebar from "../../components/sidebar/Sidebar";
import ProfileRightBar from "../../components/profileRightBar/ProfileRightBar";
import Feed from "../../components/feed/Feed";
import Loading from "../../components/Loading/Loading";
import { AuthContext } from "../../context/AuthContext";
import { authHeader } from "../../utils/authHeader";
import "./profile.scss";

import { API_BASE_URL, urlDeArchivo } from "../../config";
const DEFAULT_PROFILE = "/assets/profileCover/DefaultProfile.jpg";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/**
 * Formatea una fecha que llega del backend como "YYYY-MM-DD" (o
 * "YYYY-MM-DD HH:mm:ss") sin pasar por new Date(cadena), que interpreta la
 * fecha en UTC y termina mostrando el día anterior en zonas horarias
 * negativas como la de Colombia.
 */
const formatearFecha = (valor) => {
  if (!valor) return null;
  const partes = String(valor).slice(0, 10).split("-").map(Number);
  if (partes.length !== 3 || !partes[0] || !partes[1] || !partes[2]) return null;
  const [anio, mes, dia] = partes;
  return `${dia} de ${MESES[mes - 1]} de ${anio}`;
};

/** Años completos transcurridos desde la fecha de ingreso. */
const antiguedad = (fechaIngreso) => {
  if (!fechaIngreso) return null;
  const partes = String(fechaIngreso).slice(0, 10).split("-").map(Number);
  if (partes.length !== 3 || !partes[0]) return null;

  const [anio, mes, dia] = partes;
  const hoy = new Date();
  let anios = hoy.getFullYear() - anio;
  const mesActual = hoy.getMonth() + 1;
  if (mesActual < mes || (mesActual === mes && hoy.getDate() < dia)) anios -= 1;

  if (anios < 0) return null;
  if (anios === 0) return "Menos de un año";
  return `${anios} ${anios === 1 ? "año" : "años"}`;
};

const PESTANAS = [
  { id: "publicaciones", etiqueta: "Publicaciones" },
  { id: "informacion", etiqueta: "Información" },
  { id: "fotos", etiqueta: "Fotos" },
];

const Profile = () => {
  // Antes este componente ignoraba el parámetro de la URL y siempre pintaba
  // al usuario de la sesión, así que /profile/<quien-sea> te mostraba a ti
  // mismo y no había forma de visitar el perfil de un compañero.
  const { username } = useParams();
  const { currentUser } = useContext(AuthContext);

  const [perfil, setPerfil] = useState(null);
  const [estado, setEstado] = useState("cargando"); // cargando | ok | noEncontrado | error
  const [pestana, setPestana] = useState("publicaciones");
  const [publicaciones, setPublicaciones] = useState([]);

  // ---------------------------------------------------------------- perfil
  useEffect(() => {
    let cancelado = false;

    const cargarPerfil = async () => {
      setEstado("cargando");

      // Sin username en la URL (enlaces antiguos), se cae a la ficha del
      // usuario de la sesión para no romper nada.
      const url = username
        ? `${API_BASE_URL}/api/users/username/${encodeURIComponent(username)}`
        : currentUser?.id
        ? `${API_BASE_URL}/api/users/${currentUser.id}`
        : null;

      if (!url) {
        if (!cancelado) setEstado("noEncontrado");
        return;
      }

      try {
        const res = await fetch(url, { headers: { ...authHeader() } });
        if (cancelado) return;

        if (res.status === 404) return setEstado("noEncontrado");
        if (!res.ok) return setEstado("error");

        setPerfil(await res.json());
        setEstado("ok");
      } catch (err) {
        if (cancelado) return;
        console.error("Error al cargar el perfil:", err);
        setEstado("error");
      }
    };

    cargarPerfil();
    return () => {
      cancelado = true;
    };
  }, [username, currentUser?.id]);

  // Al cambiar de persona se vuelve siempre a la primera pestaña.
  useEffect(() => {
    setPestana("publicaciones");
  }, [username]);

  // -------------------------------------------------- fotos de la persona
  // Se piden las publicaciones para poder mostrar la cuadrícula de fotos y
  // el avance del panel lateral. El muro lo sigue gestionando <Feed/> por su
  // cuenta (likes, comentarios, edición), esto es solo lectura.
  useEffect(() => {
    if (!perfil?.id) return;
    let cancelado = false;

    fetch(`${API_BASE_URL}/api/posts?userId=${encodeURIComponent(perfil.id)}`, {
      headers: { ...authHeader() },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((datos) => {
        if (!cancelado) setPublicaciones(Array.isArray(datos) ? datos : []);
      })
      .catch(() => {
        if (!cancelado) setPublicaciones([]);
      });

    return () => {
      cancelado = true;
    };
  }, [perfil?.id]);

  const fotos = useMemo(() => {
    const lista = [];
    for (const post of publicaciones) {
      for (const medio of post.media || []) {
        const url = typeof medio === "string" ? medio : medio?.url;
        const tipo = typeof medio === "string" ? "image" : medio?.type;
        if (url && tipo !== "video") lista.push(url);
      }
    }
    return lista;
  }, [publicaciones]);

  const esPropioPerfil =
    !!perfil && String(perfil.id) === String(currentUser?.id);

  const esAdmin = perfil?.rol === "admin";

  const datosBasicos = useMemo(() => {
    if (!perfil) return [];
    return [
      { Icono: WorkOutline, etiqueta: "Cargo", valor: perfil.cargo },
      { Icono: ApartmentOutlined, etiqueta: "Área", valor: perfil.area },
      { Icono: PersonOutline, etiqueta: "Supervisor", valor: perfil.supervisor },
      { Icono: EventOutlined, etiqueta: "Ingreso", valor: formatearFecha(perfil.fechaIngreso) },
      { Icono: HistoryToggleOffOutlined, etiqueta: "Antigüedad", valor: antiguedad(perfil.fechaIngreso) },
      { Icono: PlaceOutlined, etiqueta: "Sede", valor: perfil.direccionLaboral },
      { Icono: PublicOutlined, etiqueta: "País", valor: perfil.country },
      { Icono: MailOutline, etiqueta: "Correo", valor: perfil.email },
    ].filter((d) => d.valor);
  }, [perfil]);

  // ------------------------------------------------------------- estados
  if (estado === "cargando") {
    return (
      <div className="profile">
        <Navbar />
        <div className="profileWrapper">
          <Sidebar />
          <main className="profileMain">
            <div className="profileEstado">
              <Loading />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (estado !== "ok") {
    return (
      <div className="profile">
        <Navbar />
        <div className="profileWrapper">
          <Sidebar />
          <main className="profileMain">
            <div className="profileEstado">
              <h3>
                {estado === "noEncontrado"
                  ? "Perfil no encontrado"
                  : "No se pudo cargar el perfil"}
              </h3>
              <p>
                {estado === "noEncontrado"
                  ? `No existe ningún compañero con el usuario «${username}».`
                  : "Revisa tu conexión e inténtalo de nuevo."}
              </p>
              <Link to="/" className="btnPrimario">
                Volver al inicio
              </Link>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const nombreCorto = (perfil.name || "").split(" ")[0];

  return (
    <div className="profile">
      <Navbar />

      <div className="profileWrapper">
        <Sidebar />

        <main className="profileMain">
          {/* ============ CABECERA ESTILO FACEBOOK ============ */}
          <header className="fbHeader">
            <div className="fbCover">
              <div className="fbCoverArt" />
            </div>

            <div className="fbHeaderBody">
              <div className="fbIdentidad">
                <div className="fbAvatarAro">
                  <img
                    className="fbAvatar"
                    src={urlDeArchivo(perfil.profilePicture) || DEFAULT_PROFILE}
                    alt={perfil.name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_PROFILE;
                    }}
                  />
                </div>

                <div className="fbNombreBloque">
                  <div className="fbNombreLinea">
                    <h1 className="fbNombre">{perfil.name}</h1>
                    {/* El rol solo se muestra cuando aporta información: a
                        todo el mundo se le presupone "colaborador", así que
                        etiquetar a cada persona con su rol solo añadía ruido.
                        Solo el administrador lleva distintivo. */}
                    {esAdmin && <span className="fbRol">Administrador</span>}
                  </div>
                  <p className="fbSubtitulo">
                    {perfil.cargo || "Colaborador"}
                    {perfil.area ? ` · ${perfil.area}` : ""}
                  </p>
                  <p className="fbUsuario">@{perfil.username}</p>
                </div>

                <div className="fbAcciones">
                  {esPropioPerfil ? (
                    <Link
                      to={`/profile/${perfil.username}/edit`}
                      className="btnPrimario"
                    >
                      Editar perfil
                    </Link>
                  ) : (
                    perfil.email && (
                      <a href={`mailto:${perfil.email}`} className="btnPrimario">
                        Enviar correo
                      </a>
                    )
                  )}
                  {perfil.linkedIn && (
                    <a
                      href={perfil.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btnSecundario"
                    >
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>

              <nav className="fbPestanas">
                {PESTANAS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`fbPestana ${pestana === p.id ? "activa" : ""}`}
                    onClick={() => setPestana(p.id)}
                  >
                    {p.etiqueta}
                    {p.id === "fotos" && fotos.length > 0 && (
                      <span className="fbContador">{fotos.length}</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </header>

          {/* ============ CUERPO A DOS COLUMNAS ============ */}
          <div className="fbCuerpo">
            <aside className="fbColumnaIzquierda">
              {/* Se monta el componente directamente en vez de envolverlo
                  en <Rightbar>: ese contenedor lleva display:none por
                  debajo de 1025px (está pensado para la columna derecha del
                  inicio) y aquí haría desaparecer la información en tablet
                  y móvil. */}
              <ProfileRightBar user={perfil} />

              {fotos.length > 0 && (
                <section className="fbTarjeta">
                  <div className="fbTarjetaCabecera">
                    <h2>Fotos</h2>
                    {pestana !== "fotos" && (
                      <button
                        type="button"
                        className="fbEnlaceAccion"
                        onClick={() => setPestana("fotos")}
                      >
                        Ver todas
                      </button>
                    )}
                  </div>
                  <div className="fbFotosMini">
                    {fotos.slice(0, 6).map((url, i) => (
                      <img key={`${url}-${i}`} src={urlDeArchivo(url)} alt="" loading="lazy" />
                    ))}
                  </div>
                </section>
              )}
            </aside>

            <section className="fbColumnaDerecha">
              {pestana === "publicaciones" && (
                <Feed userId={perfil.id} enPerfil />
              )}

              {pestana === "informacion" && (
                <section className="fbTarjeta">
                  <div className="fbTarjetaCabecera">
                    <h2>Información de {nombreCorto}</h2>
                  </div>
                  {datosBasicos.length > 0 ? (
                    <ul className="fbDetalle">
                      {datosBasicos.map((d) => (
                        <li key={d.etiqueta}>
                          <span className="fbDetalleIcono" aria-hidden="true">
                            <d.Icono fontSize="small" />
                          </span>
                          <span className="fbDetalleTexto">
                            <span className="fbDetalleEtiqueta">{d.etiqueta}</span>
                            <span className="fbDetalleValor">{d.valor}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="fbVacio">
                      Todavía no hay información laboral registrada.
                    </p>
                  )}
                </section>
              )}

              {pestana === "fotos" && (
                <section className="fbTarjeta">
                  <div className="fbTarjetaCabecera">
                    <h2>Fotos</h2>
                  </div>
                  {fotos.length > 0 ? (
                    <div className="fbFotosGrid">
                      {fotos.map((url, i) => (
                        <a
                          key={`${url}-${i}`}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img src={urlDeArchivo(url)} alt="" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="fbVacio">
                      {esPropioPerfil
                        ? "Todavía no has publicado ninguna foto."
                        : `${nombreCorto} todavía no ha publicado fotos.`}
                    </p>
                  )}
                </section>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
